import { ReactNode } from 'react'

import { Text, ImageWrap } from '@hhgtech/hhg-components/components'
import { Button as MantineButton } from '@hhgtech/hhg-components/mantine'
import { theme } from '@hhgtech/hhg-components/misc'
import { Box, Flex } from '@mantine/core'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { WeekOfPregnancyApi } from 'interfaces/types'

import { renderWeekHeader } from './helpers'
import {
  StyledBabyWeekDetails,
  StyledBabyWeekHeader,
  StyledDueDateCalculatorCardBodyDetails,
} from './index.styled'

type CardContentProps = {
  selectedWeek: number
  currentWeek?: number
  dueDateWeeks: Array<WeekOfPregnancyApi>
  onReachClick?: () => void
  onTrackPeriod?: () => void
}

const BabyWeekHeader = ({
  data,
  selectedWeek,
}: {
  data: WeekOfPregnancyApi
  selectedWeek: number
}) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  if (!data) return null
  return (
    <StyledBabyWeekHeader className="week-header">
      <div className="week-image">
        <ImageWrap src={data.fruit_icon} alt="fruit icon" />
      </div>
      <Text size={isMobile ? 'p3' : 'p4'} color={theme.colors.gray700}>
        {renderWeekHeader({ data, selectedWeek, isMobile, f })}
      </Text>
    </StyledBabyWeekHeader>
  )
}

export const CardContent = ({
  selectedWeek,
  currentWeek,
  dueDateWeeks,
  onReachClick,
  onTrackPeriod,
}: CardContentProps) => {
  const { formatMessage: f } = useIntl()
  const weekData = dueDateWeeks[selectedWeek - 1]
  const isMobile = useIsMobile(true)
  const currentUrl = useCurrentUrl()
  const isShowReachDueDate = currentWeek
    ? currentWeek >= 39 && selectedWeek >= 39 && selectedWeek <= 41
    : false

  const isOverPregnancy = selectedWeek >= 41 && selectedWeek <= 44
  function renderDetails(week: number) {
    const data = dueDateWeeks?.[week - 1]
    if (data) {
      return (
        <StyledBabyWeekDetails>
          {!isMobile && (
            <BabyWeekHeader data={data} selectedWeek={selectedWeek} />
          )}
          <div className="week-detail">
            <Text
              size={isMobile ? 'p3' : 'p4'}
              color={theme.colors.gray700}
              dangerouslySetInnerHTML={{ __html: data.content }}
            ></Text>
            <Link
              prefetch={false}
              href={data.recommended_article?.permalink || '#'}
            >
              <MantineButton
                color="transparent"
                size="sm"
                variant="white"
                className="pointer-event-child-none"
                data-event-category="Health Tool"
                data-event-action="Due Date - You Need to Know"
                data-event-label={currentUrl}
                sx={{
                  padding: '4px 0',
                  height: 'auto',
                  '.mantine-Button-label': {
                    whiteSpace: 'normal',
                  },
                }}
              >
                <Text
                  as="span"
                  size={isMobile ? 's3' : 's4'}
                  type="semiBold"
                  color={theme.colors.pink800}
                >
                  {f(
                    {
                      id: 'dueDatePage.week_of_pregnancy',
                      defaultMessage: '',
                    },
                    { weekNumber: selectedWeek },
                  )}
                  :{' '}
                  {f({
                    id: 'dueDatePage.all_to_know',
                    defaultMessage: 'All You Need to Know',
                  })}
                  <svg
                    className="align-middle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <path
                      d="M9.80322 8.99999L6.93746 6.13424C6.62696 5.82374 6.62696 5.31974 6.93746 5.00924C7.24796 4.69874 7.75196 4.69874 8.06246 5.00924L11.523 8.46974C11.8162 8.76299 11.8162 9.23774 11.523 9.53024L8.06246 12.9907C7.75196 13.3012 7.24796 13.3012 6.93746 12.9907C6.62696 12.6802 6.62696 12.1762 6.93746 11.8657L9.80322 8.99999Z"
                      fill={theme.colors.pink800}
                    />
                  </svg>
                </Text>
              </MantineButton>
            </Link>
          </div>
          {isShowReachDueDate && (
            <Box mt={-8}>
              <MantineButton
                variant="secondary"
                size="md"
                onClick={onReachClick}
                theme="marryBaby"
                fullWidth={isMobile}
              >
                {f({ id: 'health.dueDateCalculator.reachedDueDate' })}
              </MantineButton>
            </Box>
          )}
        </StyledBabyWeekDetails>
      )
    }
  }

  return (
    <StyledDueDateCalculatorCardBodyDetails
      key={selectedWeek}
      data-is-reach-due-date={isShowReachDueDate}
      data-is-over-pregnancy={isOverPregnancy}
    >
      {isOverPregnancy ? (
        <Flex gap={24} direction="column">
          <Text size={isMobile ? 'p3' : 'p4'} color={theme.colors.gray700}>
            {f(
              {
                id: 'pregnancyTool.dueDateCard.over.message',
                defaultMessage:
                  'Your pregnancy is over. <b>Recalculate to track your period!</b>',
              },
              {
                b: function Component(chunks: ReactNode[]) {
                  return (
                    <Text
                      size={isMobile ? 'p3' : 'p4'}
                      color={theme.colors.gray700}
                    >
                      {chunks}
                    </Text>
                  )
                },
              },
            )}
          </Text>
          <div className="btn-group">
            <MantineButton
              variant="secondary"
              size="md"
              onClick={onTrackPeriod}
              theme="marryBaby"
              fullWidth={false}
            >
              {f({ id: 'pregnancyTool.dueDateCard.trackPeriod' })}
            </MantineButton>
            {isShowReachDueDate && (
              <MantineButton
                variant="secondary"
                size="md"
                onClick={onReachClick}
                theme="marryBaby"
                fullWidth={false}
              >
                {f({ id: 'health.dueDateCalculator.reachedDueDate' })}
              </MantineButton>
            )}
          </div>
        </Flex>
      ) : (
        <Flex
          gap={isMobile ? 16 : 48}
          pl={!isMobile ? 16 : 0}
          direction={isMobile ? 'column' : 'row'}
          align="center"
        >
          {isMobile && (
            <BabyWeekHeader
              data={dueDateWeeks?.[selectedWeek - 1]}
              selectedWeek={selectedWeek}
            />
          )}
          {weekData?.icon && (
            <Box w={180} miw={180}>
              <ImageWrap
                className="baby-img-by-week"
                src={weekData.icon}
                alt={weekData.title || ''}
              />
            </Box>
          )}
          {renderDetails(selectedWeek)}
        </Flex>
      )}
    </StyledDueDateCalculatorCardBodyDetails>
  )
}
